import React, { useState, useEffect } from 'react';
import { Modal } from './Modal';

interface ColumnEditModalProps {
  isOpen: boolean;
  onClose: () => void;
  column: { id: string; title: string; color: string } | null;
  onSave: (columnId: string, title: string, color: string) => void;
}

export const ColumnEditModal: React.FC<ColumnEditModalProps> = ({
  isOpen,
  onClose,
  column,
  onSave
}) => {
  const [title, setTitle] = useState('');
  const [color, setColor] = useState('');

  useEffect(() => {
    if (column) {
      setTitle(column.title);
      setColor(column.color);
    }
  }, [column]);

  const handleSave = () => {
    if (column && title.trim()) {
      onSave(column.id, title.trim(), color);
      onClose();
    }
  };

  const predefinedColors = [
    // Primary colors
    { name: 'Red', value: '#FF3B30' },
    { name: 'Orange', value: '#FF9500' },
    { name: 'Yellow', value: '#FFCC00' },
    { name: 'Green', value: '#34C759' },
    { name: 'Blue', value: '#007AFF' },
    { name: 'Purple', value: '#AF52DE' },
    { name: 'Pink', value: '#FF2D92' },
    { name: 'Indigo', value: '#5856D6' },
    
    // Secondary colors
    { name: 'Teal', value: '#30B0C7' },
    { name: 'Cyan', value: '#32D74B' },
    { name: 'Lime', value: '#32D74B' },
    { name: 'Brown', value: '#A2845E' },
    { name: 'Gray', value: '#8E8E93' },
    { name: 'Dark Blue', value: '#1D1D1F' },
    { name: 'Coral', value: '#FF6B6B' },
    { name: 'Mint', value: '#00D4AA' }
  ];

  if (!column) return null;

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Edit Column" size="small">
      <div className="column-edit">
        <div className="form-group">
          <label>Column Title</label>
          <input
            type="text"
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            className="form-input"
            placeholder="Enter column title"
          />
        </div>

        <div className="form-group">
          <label>Column Color</label>
          <div className="color-picker">
            {predefinedColors.map((colorOption) => (
              <button
                key={colorOption.value}
                className={`color-option ${color === colorOption.value ? 'active' : ''}`}
                style={{ backgroundColor: colorOption.value }}
                onClick={() => setColor(colorOption.value)}
                title={colorOption.name}
              />
            ))}
          </div>
          
          <div className="custom-color-section">
            <label>Custom Color</label>
            <div className="custom-color-picker">
              <input
                type="color"
                value={color.startsWith('#') ? color : '#007AFF'}
                onChange={(e) => setColor(e.target.value)}
                className="color-input"
              />
              <input
                type="text"
                value={color}
                onChange={(e) => setColor(e.target.value)}
                className="form-input color-text-input"
                placeholder="#FFFFFF or rgb(255,255,255)"
              />
            </div>
          </div>
        </div>

        <div className="modal-actions">
          <button className="btn btn-secondary" onClick={onClose}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={handleSave}>
            Save Changes
          </button>
        </div>
      </div>

      <style>{`
        .column-edit {
          display: flex;
          flex-direction: column;
          gap: 20px;
        }

        .form-group {
          display: flex;
          flex-direction: column;
          gap: 6px;
        }

        .form-group label {
          font-size: 14px;
          font-weight: 500;
          color: var(--text-primary);
        }

        .color-picker {
          display: grid;
          grid-template-columns: repeat(8, 32px);
          gap: 8px;
          margin-bottom: 16px;
        }

        .color-option {
          width: 32px;
          height: 32px;
          border-radius: 50%;
          border: 2px solid var(--border-secondary);
          cursor: pointer;
          transition: all 0.2s ease;
        }

        .color-option:hover {
          transform: scale(1.1);
        }

        .color-option.active {
          border-color: var(--text-primary);
          box-shadow: 0 0 0 2px var(--bg-primary), 0 0 0 4px var(--text-primary);
        }

        .custom-color-section {
          margin-top: 8px;
        }

        .custom-color-section label {
          font-size: 13px;
          font-weight: 500;
          color: var(--text-secondary);
          margin-bottom: 8px;
        }

        .custom-color-picker {
          display: flex;
          gap: 8px;
          align-items: center;
        }

        .color-input {
          width: 48px;
          height: 32px;
          border-radius: var(--radius-small);
          border: 1px solid var(--border-secondary);
          cursor: pointer;
        }

        .color-text-input {
          flex: 1;
          max-width: 160px;
        }

        .modal-actions {
          display: flex;
          justify-content: flex-end;
          gap: 12px;
          padding-top: 16px;
          border-top: 1px solid var(--border-secondary);
        }
      `}</style>
    </Modal>
  );
};